import { useMemo } from 'react';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { getApprovalPortalDataUrl } from '@/constant/approval';
import {
  I18nText,
  Lang,
  normalizeLang,
  pickI18nText,
} from '@/utils/approval/i18nText';
import {
  ApprovalPortalRelationsData,
  RelatedApprovalItem,
} from '@/types/approval';
import { getNestedDataFetcher } from '@/utils/apis';

/**
 * 편집 모드 진입 시 연관품의(parents/children/related) 3 카테고리를 한 번에 조회.
 */
const EMPTY: RelatedApprovalItem[] = [];
type RawRelatedApprovalItem = {
  docId: string;
  subject: string | I18nText | null | undefined;
  status?: string;
  author?: { name?: string | I18nText | null } | null;
  authoredAt: string;
};

type RawRelations = Omit<
  ApprovalPortalRelationsData,
  'parents' | 'children' | 'related'
> & {
  parents?: RawRelatedApprovalItem[];
  children?: RawRelatedApprovalItem[];
  related?: RawRelatedApprovalItem[];
};

const toItem = (
  raw: RawRelatedApprovalItem,
  lang: Lang,
): RelatedApprovalItem => ({
  docId: raw.docId,
  subject: pickI18nText(raw.subject, lang),
  status: raw.status,
  author: { name: pickI18nText(raw.author?.name, lang) },
  authoredAt: raw.authoredAt,
});

const useRelatedApprovals = (documentId?: string | null) => {
  const { i18n } = useTranslation();
  const lang = useMemo(() => normalizeLang(i18n.language), [i18n.language]);

  const { data, error, isLoading, mutate } = useSWR<RawRelations>(
    documentId ? getApprovalPortalDataUrl(documentId) : null,
    (url: string) => getNestedDataFetcher<RawRelations>(url),
  );

  const parents = useMemo(
    () =>
      data?.parents && data.parents.length > 0
        ? data.parents.map((it) => toItem(it, lang))
        : EMPTY,
    [data?.parents, lang],
  );
  const children = useMemo(
    () =>
      data?.children && data.children.length > 0
        ? data.children.map((it) => toItem(it, lang))
        : EMPTY,
    [data?.children, lang],
  );
  const related = useMemo(
    () =>
      data?.related && data.related.length > 0
        ? data.related.map((it) => toItem(it, lang))
        : EMPTY,
    [data?.related, lang],
  );

  return {
    parents,
    children,
    related,
    isLoading,
    error,
    mutate,
  };
};

export default useRelatedApprovals;
